import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Clock, Calendar, TrendingUp, Building2, 
  BarChart3, BookOpen, ChevronRight, Search, Filter,
  MapPin, DollarSign, Users, Briefcase, LineChart, Target,
  RefreshCw
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';

const InsightsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Fetch insights from CMS
  useEffect(() => {
    const fetchInsights = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/content/insights`);
        if (response.ok) {
          const data = await response.json();
          if (data.length > 0) {
            setInsights(data);
          } else {
            // Fall back to mock data if CMS is empty
            setInsights(mockArticles);
          }
        } else {
          setInsights(mockArticles);
        }
      } catch (err) {
        console.error('Failed to fetch insights:', err);
        setInsights(mockArticles);
      } finally {
        setLoading(false);
      }
    };

    fetchInsights();
  }, [API_URL]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  // Categories
  const categories = [
    { id: 'all', name: 'All Insights', icon: BookOpen },
    { id: 'Market Analysis', name: 'Market Analysis', icon: MapPin },
    { id: 'Investment Strategy', name: 'Investment Strategy', icon: Target },
    { id: 'Industry News', name: 'Industry News', icon: LineChart },
    { id: 'Fund Updates', name: 'Fund Updates', icon: Users }
  ];

  // Mock articles (fallback when CMS is empty)
  const mockArticles = [
    {
      id: '1',
      title: 'Why Houston Remains a Top Multifamily Market in 2025',
      excerpt: 'Population growth, job expansion, and affordable housing demand continue to drive exceptional opportunities in the Houston metro area for value-add investors.',
      category: 'Market Analysis',
      created_at: '2025-01-15T00:00:00Z',
      slug: 'houston-multifamily-market-2025',
      featured_image: 'https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=1200&q=80',
      author: 'ASAP Capital Research',
      content_type: 'insight',
      tags: ['houston', 'multifamily', 'market analysis']
    },
    {
      id: '2',
      title: 'What Is Value-Add Real Estate Investing?',
      excerpt: 'Understanding the strategy behind acquiring underperforming assets and creating value through renovations and operational improvements.',
      category: 'Investment Strategy',
      created_at: '2025-01-10T00:00:00Z',
      slug: 'value-add-investing-guide',
      featured_image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80',
      author: 'ASAP Capital Research',
      content_type: 'insight',
      tags: ['value-add', 'strategy', 'education']
    },
    {
      id: '3',
      title: 'How Interest Rates Affect Commercial Real Estate',
      excerpt: 'Analyzing the relationship between Fed policy, cap rates, and property valuations in the current economic environment.',
      category: 'Industry News',
      created_at: '2025-01-05T00:00:00Z',
      slug: 'interest-rates-commercial-real-estate',
      featured_image: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80',
      author: 'ASAP Capital Research',
      content_type: 'insight',
      tags: ['interest rates', 'economics', 'market analysis']
    },
    {
      id: '4',
      title: 'Greater Houston Multifamily Trends: Q4 2024 Review',
      excerpt: 'Rental demand, occupancy rates, and rent growth across Houston submarkets as we enter 2025.',
      category: 'Market Analysis',
      created_at: '2024-12-28T00:00:00Z',
      slug: 'houston-multifamily-q4-2024',
      featured_image: 'https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&q=80',
      author: 'ASAP Capital Research',
      content_type: 'insight',
      tags: ['houston', 'multifamily', 'quarterly review']
    },
    {
      id: '5',
      title: 'Understanding IRR vs Cash-on-Cash Returns',
      excerpt: 'A guide for accredited investors on how to evaluate private real estate investment opportunities.',
      category: 'Investment Strategy',
      created_at: '2024-12-20T00:00:00Z',
      slug: 'irr-vs-cash-on-cash',
      featured_image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      author: 'ASAP Capital Research',
      content_type: 'insight',
      tags: ['education', 'returns', 'investor guide']
    },
    {
      id: '6',
      title: 'The Role of Job Growth in Multifamily Demand',
      excerpt: 'How employment trends in Texas metros influence rental demand and investment thesis.',
      category: 'Market Analysis',
      created_at: '2024-12-15T00:00:00Z',
      slug: 'job-growth-multifamily-demand',
      featured_image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
      author: 'ASAP Capital Research',
      content_type: 'insight',
      tags: ['jobs', 'demand', 'texas']
    }
  ];

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Estimate read time
  const getReadTime = (content) => {
    if (!content) return '5 min read';
    const wordCount = content.split(/\s+/).length;
    const minutes = Math.ceil(wordCount / 200);
    return `${minutes} min read`;
  };

  // Filter articles
  const filteredArticles = insights.filter(article => {
    const matchesCategory = activeCategory === 'all' || article.category === activeCategory;
    const matchesSearch = !searchQuery || 
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get featured article (first one)
  const featuredArticle = filteredArticles[0];
  const otherArticles = filteredArticles.slice(1);

  // Get category icon
  const getCategoryIcon = (categoryName) => {
    const category = categories.find(c => c.id === categoryName);
    return category?.icon || BookOpen;
  };

  return (
    <>
      <Helmet>
        <title>Market Insights & Research | ASAP Capital</title>
        <meta name="description" content="Expert analysis and research on Texas real estate markets, multifamily trends, and investment strategies from ASAP Capital's research team." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "ASAP Capital Insights",
            "description": "Expert real estate investment research and market analysis",
            "publisher": {
              "@type": "Organization",
              "name": "ASAP Capital",
              "url": "https://asapcapital.net"
            }
          })}
        </script>
      </Helmet>

      <Header />

      <main className="bg-[#0A1628] min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#111D35] to-[#0A1628]" />
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-1/4 w-64 h-64 bg-[#D4AF37]/5 rounded-full blur-2xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              className="text-center max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 mb-6">
                <BookOpen className="h-4 w-4 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-medium">Research & Insights</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Market Intelligence for
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-[#B8962E]">
                  Informed Investors
                </span>
              </h1>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                Data-driven analysis and expert commentary on Texas real estate markets, 
                multifamily trends, and value-add investment strategies.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Categories & Search */}
        <section className="relative py-8 border-y border-white/10 bg-[#111D35]/50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              {/* Categories */}
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                        activeCategory === category.id
                          ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/30'
                          : 'bg-white/5 text-white/60 border border-white/10 hover:bg-white/10 hover:text-white'
                      }`}
                      data-testid={`category-${category.id}`}
                    >
                      <Icon className="h-4 w-4" />
                      {category.name}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <input
                  type="text"
                  placeholder="Search insights..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-white/40 focus:outline-none focus:border-[#D4AF37]/50 w-full lg:w-64"
                  data-testid="insights-search"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Loading State */}
        {loading && (
          <div className="py-20 text-center">
            <RefreshCw className="h-8 w-8 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <p className="text-white/60">Loading insights...</p>
          </div>
        )}

        {/* Content Grid */}
        {!loading && (
          <section className="py-16 lg:py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {filteredArticles.length === 0 ? (
                <div className="text-center py-20">
                  <BookOpen className="h-16 w-16 text-white/20 mx-auto mb-4" />
                  <h3 className="text-xl text-white mb-2">No insights found</h3>
                  <p className="text-white/60">Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                <motion.div
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  className="space-y-12"
                >
                  {/* Featured Article */}
                  {featuredArticle && (
                    <motion.div variants={itemVariants}>
                      <Link 
                        to={`/insights/${featuredArticle.slug}`}
                        className="group block"
                        data-testid="featured-insight"
                      >
                        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#111D35] to-[#0A1628] border border-white/10 hover:border-[#D4AF37]/30 transition-all duration-300">
                          <div className="grid lg:grid-cols-2 gap-0">
                            {/* Image */}
                            <div className="relative h-64 lg:h-96 overflow-hidden">
                              {featuredArticle.featured_image ? (
                                <img 
                                  src={featuredArticle.featured_image} 
                                  alt={featuredArticle.title}
                                  className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
                                />
                              ) : (
                                <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/20 to-transparent flex items-center justify-center">
                                  <BookOpen className="h-16 w-16 text-[#D4AF37]/30" />
                                </div>
                              )}
                              <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/80 via-[#0A1628]/40 to-transparent lg:hidden" />
                            </div>

                            {/* Content */}
                            <div className="relative p-8 lg:p-12 flex flex-col justify-center">
                              <div className="flex items-center gap-3 mb-4">
                                <span className="px-3 py-1 rounded-full bg-[#D4AF37]/20 text-[#D4AF37] text-xs font-medium border border-[#D4AF37]/30">
                                  Featured
                                </span>
                                <span className="px-3 py-1 rounded-full bg-white/5 text-white/60 text-xs font-medium border border-white/10">
                                  {featuredArticle.category}
                                </span>
                              </div>
                              <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4 group-hover:text-[#D4AF37] transition-colors">
                                {featuredArticle.title}
                              </h2>
                              <p className="text-white/70 mb-6 line-clamp-3">
                                {featuredArticle.excerpt}
                              </p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4 text-white/50 text-sm">
                                  <span className="flex items-center gap-1">
                                    <Calendar className="h-4 w-4" />
                                    {formatDate(featuredArticle.publish_date || featuredArticle.created_at)}
                                  </span>
                                  <span className="flex items-center gap-1">
                                    <Clock className="h-4 w-4" />
                                    {getReadTime(featuredArticle.content)}
                                  </span>
                                </div>
                                <span className="flex items-center gap-2 text-[#D4AF37] font-medium group-hover:gap-3 transition-all">
                                  Read Article
                                  <ArrowRight className="h-4 w-4" />
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Link>
                    </motion.div>
                  )}

                  {/* Other Articles Grid */}
                  {otherArticles.length > 0 && (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {otherArticles.map((article, index) => {
                        const CategoryIcon = getCategoryIcon(article.category);
                        return (
                          <motion.div
                            key={article.id}
                            variants={itemVariants}
                          >
                            <Link 
                              to={`/insights/${article.slug}`}
                              className="group block h-full"
                              data-testid={`insight-card-${index}`}
                            >
                              <div className="h-full bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl border border-white/10 overflow-hidden hover:border-[#D4AF37]/30 transition-all duration-300">
                                {/* Image */}
                                <div className="relative h-48 overflow-hidden">
                                  {article.featured_image ? (
                                    <img 
                                      src={article.featured_image} 
                                      alt={article.title}
                                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-[#D4AF37]/10 to-transparent flex items-center justify-center">
                                      <CategoryIcon className="h-12 w-12 text-[#D4AF37]/30" />
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-transparent to-transparent" />
                                  <div className="absolute bottom-3 left-3">
                                    <span className="px-2 py-1 rounded-full bg-white/10 backdrop-blur-sm text-white/80 text-xs font-medium">
                                      {article.category}
                                    </span>
                                  </div>
                                </div>

                                {/* Content */}
                                <div className="p-5">
                                  <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2 group-hover:text-[#D4AF37] transition-colors">
                                    {article.title}
                                  </h3>
                                  <p className="text-white/60 text-sm mb-4 line-clamp-2">
                                    {article.excerpt}
                                  </p>
                                  <div className="flex items-center justify-between text-white/40 text-xs">
                                    <span className="flex items-center gap-1">
                                      <Calendar className="h-3 w-3" />
                                      {formatDate(article.publish_date || article.created_at)}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {getReadTime(article.content)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}
                </motion.div>
              )}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="py-16 lg:py-20 border-t border-white/10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                Stay Informed on Market Opportunities
              </h2>
              <p className="text-white/70 mb-8 max-w-2xl mx-auto">
                Connect with our team to discuss how these market insights translate into 
                investment opportunities for accredited investors.
              </p>
              <Link to="/contact">
                <Button className="cta-contact">
                  Request Investment Information
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default InsightsPage;
